import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { trackEvent } from '../services/analytics';
import { CreateSearchWizard } from './CreateSearchWizard';
import { EXTENSION_URL } from './landing/landingData';
import type { FlatSearch } from '../types/api';

declare global {
  interface Window {
    __MOVETWO_EXTENSION__?: boolean;
  }
}

interface EmptyStateProps {
  onSearchCreated: (search: FlatSearch) => void;
  onSearchJoined: (search: FlatSearch) => void;
}

const POPULAR_SITES = [
  { name: 'Rightmove', url: 'https://www.rightmove.co.uk' },
  { name: 'Zoopla', url: 'https://www.zoopla.co.uk' },
  { name: 'Zillow', url: 'https://www.zillow.com' },
  { name: 'Realtor.com', url: 'https://www.realtor.com' },
  { name: 'SeLoger', url: 'https://www.seloger.com' },
  { name: 'ImmobilienScout24', url: 'https://www.immobilienscout24.de' },
];

export function EmptyState({ onSearchCreated, onSearchJoined }: EmptyStateProps) {
  const { t } = useTranslation();
  const [showWizard, setShowWizard] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [installChecking, setInstallChecking] = useState(false);
  const [extensionDetected, setExtensionDetected] = useState(false);
  const [installTimeout, setInstallTimeout] = useState(false);

  const handleInstallClick = useCallback(() => {
    setInstallChecking(true);
    setExtensionDetected(false);
    setInstallTimeout(false);
  }, []);

  useEffect(() => {
    if (!installChecking) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      if (window.__MOVETWO_EXTENSION__) {
        clearInterval(interval);
        setInstallChecking(false);
        setExtensionDetected(true);
        trackEvent('extension_installed');
      } else if (Date.now() - startTime >= 30000) {
        clearInterval(interval);
        setInstallChecking(false);
        setInstallTimeout(true);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [installChecking]);

  const handleJoin = async () => {
    const code = inviteCode.trim();
    if (!code) return;
    setJoining(true);
    setJoinError('');
    try {
      const search = await api.joinSearch(code);
      setInviteCode('');
      onSearchJoined(search);
    } catch {
      setJoinError(t('app.emptyState.invalidCode'));
    } finally {
      setJoining(false);
    }
  };

  if (showWizard) {
    return (
      <CreateSearchWizard
        onComplete={onSearchCreated}
        onCancel={() => setShowWizard(false)}
      />
    );
  }

  return (
    <div className="empty-state-welcome">
      <h2 className="empty-state-heading">{t('app.emptyState.welcome')}</h2>
      <p className="empty-state-subtext">{t('app.emptyState.subtitle')}</p>

      <div className="empty-state-steps">
        {/* Step 1: Install Extension */}
        <div className="empty-state-step-card">
          <div className="empty-state-step-number">
            {extensionDetected ? '\u2713' : '1'}
          </div>
          <h3 className="empty-state-step-title">{t('app.emptyState.step1Title')}</h3>
          <p className="empty-state-step-desc">{t('app.emptyState.step1Desc')}</p>

          {installChecking ? (
            <div className="empty-state-install-status">
              <span className="empty-state-spinner" aria-hidden="true" />
              <span>{t('app.emptyState.checkingInstall')}</span>
            </div>
          ) : extensionDetected ? (
            <div className="empty-state-install-status empty-state-install-success">
              <span aria-hidden="true">{'\u2713'}</span>
              <span>{t('app.emptyState.extensionDetected')}</span>
            </div>
          ) : installTimeout ? (
            <div className="empty-state-install-status">
              <span>{t('app.emptyState.installTimeout')}</span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => window.location.reload()}
                style={{ marginInlineStart: '0.5rem' }}
              >
                {'\u21BB'}
              </button>
            </div>
          ) : (
            <a
              className="btn btn-primary btn-md"
              href={EXTENSION_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleInstallClick}
            >
              {t('app.emptyState.installExtension')} &rarr;
            </a>
          )}
        </div>

        {/* Step 2: Create or Join */}
        <div className="empty-state-step-card">
          <div className="empty-state-step-number">2</div>
          <h3 className="empty-state-step-title">{t('app.emptyState.step2Title')}</h3>

          <div className="empty-state-cards">
            <div className="empty-state-card">
              <h3>{t('app.emptyState.createTitle')}</h3>
              <p className="empty-state-card-desc">
                {t('app.emptyState.createDesc')}
              </p>
              <button
                className="btn btn-primary btn-md"
                onClick={() => setShowWizard(true)}
              >
                {t('app.emptyState.getStarted')}
              </button>
            </div>

            <div className="empty-state-card">
              <h3>{t('app.emptyState.joinTitle')}</h3>
              <p className="empty-state-card-desc">
                {t('app.emptyState.joinDesc')}
              </p>
              <form
                className="empty-state-form"
                onSubmit={(e) => { e.preventDefault(); handleJoin(); }}
              >
                <input
                  className="input"
                  type="text"
                  placeholder={t('app.emptyState.joinPlaceholder')}
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  disabled={joining}
                />
                {joinError && <p className="empty-state-error">{joinError}</p>}
                <button
                  className="btn btn-primary btn-md"
                  type="submit"
                  disabled={joining || !inviteCode.trim()}
                >
                  {joining ? t('app.emptyState.joining') : t('app.emptyState.join')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Popular sites */}
      <p className="empty-state-sites-prompt">{t('app.emptyState.sitesPrompt')}</p>
      <div className="empty-state-sites">
        {POPULAR_SITES.map((site) => (
          <a
            key={site.name}
            className="empty-state-site-link"
            href={site.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {site.name}
          </a>
        ))}
      </div>

      <p className="disclaimer-text">
        {t('app.emptyState.disclaimer')}
      </p>
    </div>
  );
}
